/**
 * CombativesShell — shared primitives for the Wrestling/Boxing-pattern journals.
 *
 * These components are byte-identical across WrestlingLogDialog and
 * BoxingLogDialog. Centralised here so future edits happen in one place.
 *
 * BJJ, Judo, and Muay Thai use an older architecture (no CollapsibleSection,
 * structured reflection objects, autocomplete from history). They are NOT
 * served by this file — that migration is Phase 2 of the combatives refactor.
 *
 * Phase 2 plan: build a full `<CombativesJournalShell config={}>` wrapper that
 * Wrestling and Boxing become thin config wrappers around. Reference the
 * MASTER_BACKLOG.md "Combatives factor refactor" entry for scope details.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Converts an ISO timestamp to a human-readable relative string.
 * Shared across all 5 journal dialogs (minor implementation variations in
 * BJJ/Judo/MT — those will align when those arts migrate to this file).
 */
export const formatTimeAgo = (iso) => {
  if (!iso) return 'earlier';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'a moment ago';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Collapsible section wrapper used throughout Wrestling and Boxing journals.
 * Sections default closed unless `defaultOpen` is true.
 */
export const CollapsibleSection = ({ title, children, testId, defaultOpen = false, icon = null }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-enf-surface-border rounded-lg overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-2.5 flex items-center gap-2 bg-enf-surface-mid hover:bg-enf-surface-elevated text-left"
        data-testid={`${testId}-toggle`}
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-enf-text-muted" />
          : <ChevronRight className="w-4 h-4 text-enf-text-muted" />}
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </button>
      {open && <div className="p-3 bg-enf-surface-deep">{children}</div>}
    </div>
  );
};

/**
 * Labelled number input cell used inside sparring stat grids.
 * Shared between WrestlingSparringBlock and BoxingSparringBlock.
 */
export const NumCell = ({ k, label, value, set, testId }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">{label}</span>
    <input
      type="number"
      min={0}
      value={value[k] ?? ''}
      onChange={(e) => set({ [k]: e.target.value })}
      className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs text-enf-text-primary"
      data-testid={testId}
    />
  </label>
);
